import { allNavItems } from '@/app/navigation';

/**
 * As caixas do Perfil da Corretora do OnPoint (`/parametrizacao/broker-profile`) e as
 * permissoes do SeguraCore por tras de cada uma (PAR-115 a PAR-135).
 *
 * O OnPoint tem uma caixa por funcionalidade; o catalogo do SeguraCore e mais fino, e
 * uma caixa pode valer por mais de uma chave ("Gerenciar usuarios" e criar e editar).
 * A caixa fica marcada quando o perfil tem todas as chaves, e pela metade quando tem
 * so parte. O que o SeguraCore tem a mais aparece em "Outras permissoes do
 * SeguraCore", no fim: a tela mostra tudo, e nao so o que o OnPoint tinha.
 */
export interface CaixaDoPerfil {
  rotulo: string;
  chaves: string[];
  /**
   * Por que a caixa nao pode ser marcada no perfil da corretora do SeguraCore. A
   * caixa aparece, desabilitada, com o motivo.
   */
  indisponivel?: string;
  /** Caixa que o OnPoint so mostra nos dialogos de cadastro e edicao. */
  soNoDialogo?: boolean;
  /** Nota sobre o que a caixa abrange no SeguraCore. */
  nota?: string;
}

export interface GrupoDoPerfil {
  titulo: string;
  caixas: CaixaDoPerfil[];
}

/** Os grupos na ordem do OnPoint. */
export const gruposDoPerfilDaCorretora: GrupoDoPerfil[] = [
  {
    titulo: 'Propostas',
    caixas: [
      {
        rotulo: 'Gerenciar propostas',
        chaves: ['operacoes.operacao.create'],
        nota: 'Na corretora, gerenciar e abrir a proposta. Editar a cotacao depende de decisao da seguradora (PAR-122).',
      },
      { rotulo: 'Visualizar propostas', chaves: ['operacoes.operacao.view'] },
    ],
  },
  {
    titulo: 'Apolices',
    caixas: [
      {
        rotulo: 'Visualizar apolices',
        chaves: ['apolices.apolice.view', 'fiancas.fianca.view'],
      },
      { rotulo: 'Solicitar endosso', chaves: ['endossos.endosso.create'] },
      {
        rotulo: 'Bloqueio de apolice para renovacao',
        chaves: ['renovacoes.bloqueio.update'],
        nota: 'Guardada no perfil. O bloqueio da apolice ainda nao tem efeito: a regra nao tem fonte (PAR-126).',
      },
    ],
  },
  {
    titulo: 'Corretores',
    caixas: [
      {
        rotulo: 'Gerenciar dados da corretora',
        chaves: ['corretores.corretor.update'],
        nota: 'A conta bancaria da corretora fica em Outras permissoes: o padrao e so a seguradora trocar o destino do repasse.',
      },
      { rotulo: 'Visualizar dados da corretora', chaves: ['corretores.corretor.view'] },
    ],
  },
  {
    titulo: 'Tomador',
    caixas: [
      { rotulo: 'Visualizar tomadores', chaves: ['tomadores.tomador.view'] },
      {
        rotulo: 'Gerenciar CCG',
        chaves: [],
        soNoDialogo: true,
        indisponivel:
          'A CCG ainda nao respeita o escopo da corretora, e o que a corretora faz nela esta a confirmar (PAR-130).',
      },
    ],
  },
  {
    titulo: 'Usuario',
    caixas: [
      { rotulo: 'Visualizar usuarios', chaves: ['usuarios.usuario.view'] },
      {
        rotulo: 'Gerenciar usuarios',
        chaves: ['usuarios.usuario.create', 'usuarios.usuario.update'],
      },
    ],
  },
  {
    titulo: 'Documentos',
    caixas: [
      { rotulo: 'Visualizar documentos', chaves: ['documentos.seguradora.view'] },
    ],
  },
  {
    titulo: 'Gerenciamento de Erro de Consulta',
    caixas: [
      {
        rotulo: 'Habilitar filtro mandatorio',
        chaves: ['integracoes.filtro_mandatorio.update'],
        soNoDialogo: true,
        indisponivel:
          'A corretora nao ve as consultas a bureau no SeguraCore, entao a caixa nao teria efeito. Para a seguradora, ela fica no perfil em Usuarios e perfis.',
      },
    ],
  },
];

/** Todas as chaves que alguma caixa do OnPoint cobre. */
export function chavesDasCaixas(): Set<string> {
  return new Set(
    gruposDoPerfilDaCorretora.flatMap((grupo) => grupo.caixas.flatMap((c) => c.chaves)),
  );
}

export type EstadoDaCaixa = 'marcada' | 'parcial' | 'desmarcada';

/** Estado da caixa pelo conjunto de permissoes do perfil. */
export function estadoDaCaixa(
  caixa: CaixaDoPerfil,
  marcadas: Set<string>,
): EstadoDaCaixa {
  if (caixa.chaves.length === 0) return 'desmarcada';
  const tem = caixa.chaves.filter((chave) => marcadas.has(chave)).length;
  if (tem === caixa.chaves.length) return 'marcada';
  return tem === 0 ? 'desmarcada' : 'parcial';
}

/**
 * Alterna a caixa: marcada sai inteira; desmarcada ou pela metade entra inteira, so
 * com as chaves que valem para a corretora.
 */
export function alternarCaixa(
  caixa: CaixaDoPerfil,
  marcadas: Set<string>,
  disponiveis: Set<string>,
): Set<string> {
  const proximo = new Set(marcadas);
  if (estadoDaCaixa(caixa, marcadas) === 'marcada') {
    for (const chave of caixa.chaves) proximo.delete(chave);
  } else {
    for (const chave of caixa.chaves) if (disponiveis.has(chave)) proximo.add(chave);
  }
  return proximo;
}

/**
 * Opcoes do "Home menu": as telas do menu da corretora que as permissoes marcadas
 * alcancam. Sem nenhuma, o OnPoint pede "Atribua alguma funcionalidade ao perfil para
 * selecionar o home".
 */
export function telasIniciaisDaCorretora(
  marcadas: Set<string>,
): { value: string; label: string }[] {
  return allNavItems()
    .filter((item) => item.audience !== 'insurer' && marcadas.has(item.permission))
    .map((item) => ({ value: item.path, label: item.label }));
}
